import React from 'react';
import { Divider, Image, Radio, Slider, Switch } from 'antd';
import useMapSetting, { EMapType } from '../../../zustand/useMap';
import { ReactComponent as IconVesselOnline } from '../../../assets/icons/home/icon-vessel-online.svg';
import { ReactComponent as IconVesselOffline } from '../../../assets/icons/home/icon-vessel-offline.svg';
import { ReactComponent as IconVesselWaiting } from '../../../assets/icons/home/icon-vessel-waiting.svg';
import { ReactComponent as IconVesselLabel } from '../../../assets/icons/home/icon-vessel-label.svg';
import { ReactComponent as IconVesselTrail } from '../../../assets/icons/home/icon-vessel-trail.svg';
import { ReactComponent as IconDotTrail } from '../../../assets/icons/home/icon-dot-trail.svg';
import MapSatellite from '../../../assets/icons/home/map-satellite.png';
import MapVector from '../../../assets/icons/home/map-vector.png';
import MapVectorWhite from '../../../assets/icons/home/map-vector-white.png';
import { capitalizeFirstLetter } from '@qlibs/react-components';
import styled from 'styled-components';

type MapSettingsProps = {
  compact?: boolean;
};

const MapSettings: React.FC<MapSettingsProps> = ({ compact = false }) => {
  const {
    showVesselOnline,
    showVesselOffline,
    showVesselWaiting,
    showVesselLabel,
    showDotTrail,
    trailWidth,
    trailOpacity,
    trailLength,
    mapType,
    setShowVesselOnline,
    setShowVesselOffline,
    setShowVesselWaiting,
    setShowVesselLabel,
    setShowDotTrail,
    setTrailWidth,
    setTrailOpacity,
    setTrailLength,
    setMapType,
  } = useMapSetting();

  const _renderSwitchSetting = (icon: any, label: string, value: boolean) => {
    const handleSwitchChange = (label: string, val: boolean) => {
      const labelMap: Record<string, (val: boolean) => void> = {
        'Show Online Vessel': setShowVesselOnline,
        'Show Offline Vessel': setShowVesselOffline,
        'Show Waiting Data Vessel': setShowVesselWaiting,
        'Show Vessel Label': setShowVesselLabel,
        // 'Show Vessel Trail': setShowVesselTrail,
        'Show Vessel Trail': setShowDotTrail,
      };

      const handler = Object.keys(labelMap).find((key) => label.includes(key));

      if (handler && labelMap[handler]) {
        labelMap[handler](val);

        if (
          [
            'Show Online Vessel',
            'Show Offline Vessel',
            'Show Waiting Data Vessel',
          ].includes(handler)
        ) {
          setShowVesselLabel(val);
        }
      }
    };

    return (
      <SwitchSettingRow $compact={compact}>
        <SwitchSettingLabel $compact={compact}>
          {icon}
          <div style={{ fontSize: compact ? 12 : 14 }}>{label}</div>
        </SwitchSettingLabel>

        <Switch
          size='small'
          checked={value}
          onChange={(val: boolean) => handleSwitchChange(label, val)}
        />
      </SwitchSettingRow>
    );
  };

  const _renderRangeSetting = (
    label: string,
    value: any,
    min: any,
    max: any,
    step: number = 1
  ) => {
    return (
      <div
        style={{
          marginBottom: compact ? 10 : 15,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: compact ? 6 : 10,
          }}
        >
          <div style={{ fontSize: compact ? 12 : 14 }}>{label}</div>
          <div
            style={{
              backgroundColor: 'rgb(118, 132, 153)',
              padding: compact ? '2px 5px' : '4px 6px 4px 6px',
              borderRadius: 5,
              textAlign: 'center',
              color: 'white',
              fontSize: compact ? 11 : 14,
            }}
          >
            {value}
          </div>
        </div>
        <Slider
          style={{ margin: compact ? '4px 6px 8px' : undefined }}
          min={min}
          max={max}
          defaultValue={value}
          step={step}
          onChange={(e) => {
            if (label.includes('Width')) {
              setTrailWidth(e);
            } else if (label.includes('Opacity')) {
              setTrailOpacity(e);
            } else {
              setTrailLength(e);
            }
          }}
        />
      </div>
    );
  };

  const _renderMapSetting = (icon: any, label: EMapType, value: string) => {
    return (
      <div
        style={{
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={() => setMapType(label)}
      >
        <Image
          preview={false}
          fallback='/images/empty-image.png'
          src={icon}
          width={compact ? 44 : 60}
          height={compact ? 44 : 60}
          style={{
            borderRadius: label === value ? 13 : 13,
            border: label === value ? '2px solid #264284' : 'none',
          }}
        />
        <p
          style={{
            marginTop: compact ? 4 : 8,
            textAlign: 'center',
            color: label === value ? '#264284' : 'black',
            fontSize: compact ? 11 : 14,
          }}
        >
          {capitalizeFirstLetter(label.split('_').join(' '))}
        </p>
      </div>
    );
  };

  const _renderTrailLength = () => {
    return (
      <>
        <div style={{ fontSize: compact ? 12 : 14, marginBottom: compact ? 10 : 20 }}>
          Trail Length
        </div>
        <Radio.Group
          value={trailLength}
          onChange={(e) => setTrailLength(e.target.value)}
        >
          <Radio style={{ marginBottom: compact ? 6 : 10, fontSize: compact ? 12 : 14 }} value={24}>
            24 hour
          </Radio>
          <Radio style={{ marginBottom: compact ? 6 : 10, fontSize: compact ? 12 : 14 }} value={48}>
            48 hour
          </Radio>
          <Radio style={{ marginBottom: compact ? 6 : 10, fontSize: compact ? 12 : 14 }} value={72}>
            72 hour
          </Radio>
        </Radio.Group>
      </>
    );
  };

  return (
    <SettingsContainer>
      <SettingsContent $compact={compact}>
        <div>
          <p style={{ color: '#748494', fontSize: compact ? 12 : 14 }}>
            Vessel Status
          </p>
          {_renderSwitchSetting(
            <IconVesselOnline />,
            'Show Online Vessel',
            showVesselOnline
          )}
          {_renderSwitchSetting(
            <IconVesselOffline />,
            'Show Offline Vessel',
            showVesselOffline
          )}
          {_renderSwitchSetting(
            <IconVesselWaiting />,
            'Show Waiting Data Vessel',
            showVesselWaiting
          )}
        </div>

        <Divider style={{ margin: compact ? '8px 0' : '15px 0' }} />

        <div>
          <p style={{ color: '#748494', fontSize: compact ? 12 : 14 }}>
            Vessel Element
          </p>
          {_renderSwitchSetting(
            <IconVesselLabel />,
            'Show Vessel Label',
            showVesselLabel
          )}
          {_renderSwitchSetting(
            <IconDotTrail />,
            'Show Vessel Trail',
            showDotTrail
          )}
        </div>

        <Divider style={{ margin: compact ? '8px 0' : '15px 0' }} />

        <div>
          <p style={{ color: '#748494', fontSize: compact ? 12 : 14 }}>
            Trail Settings
          </p>
          {_renderRangeSetting('Trail Width', trailWidth, 1, 5)}
          {_renderRangeSetting('Trail Opacity', trailOpacity, 0.5, 1, 0.1)}
          {_renderTrailLength()}
        </div>

        {/* <Divider style={{ margin: '15px 0' }} />

      <div>
        <p style={{ color: '#748494' }}>Map Type</p>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 15,
          }}
        >
          {_renderMapSetting(MapSatellite, EMapType.satellite, mapType)}
          {_renderMapSetting(MapVector, EMapType.vector_color, mapType)}
          {_renderMapSetting(MapVectorWhite, EMapType.vector_white, mapType)}
        </div>
      </div> */}
      </SettingsContent>
    </SettingsContainer>
  );
};

const SettingsContainer = styled.div`
  width: 100%;
  min-width: 0;
  height: auto;
`;

const SettingsContent = styled.div<{ $compact?: boolean }>`
  min-width: 0;
  height: 100%;
  overflow: auto;
  margin-top: ${({ $compact }) => ($compact ? '6px' : '10px')};
  padding: ${({ $compact }) => ($compact ? '0 4px 8px' : '0 10px 12px')};

  @media (max-width: 767px) {
    height: auto;
    overflow: visible;
  }
`;

const SwitchSettingRow = styled.div<{ $compact?: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: ${({ $compact }) => ($compact ? '8px' : '12px')};
  margin-bottom: ${({ $compact }) => ($compact ? '8px' : '15px')};

  @media (min-width: 768px) {
    display: flex;
    justify-content: space-between;
  }
`;

const SwitchSettingLabel = styled.div<{ $compact?: boolean }>`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${({ $compact }) => ($compact ? '6px' : '10px')};

  > svg {
    flex: 0 0 auto;
    width: ${({ $compact }) => ($compact ? '14px' : 'auto')};
    height: ${({ $compact }) => ($compact ? '14px' : 'auto')};
  }

  > div {
    overflow-wrap: anywhere;
  }
`;

export default MapSettings;
